import { useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { MediaItem } from "../models/Post";
import { MediaPlayer } from "./MediaPlayer";

const DEFAULT_ASPECT = 4 / 5;

/**
 * Non-interactive live render of a single slide (background + text/image
 * overlays) for list and calendar thumbnails. Sized by the caller via
 * className (typically a fixed height); width follows the slide's real
 * aspect ratio so the box hugs the slide with no letterbox bars.
 *
 * The rendered image is measured directly and wins over metadata.dimensions
 * once loaded, guarding against posts whose metadata is missing or stale.
 */
export function SlidePreview({
  mediaItem,
  mediaUrls,
  className,
  eagerOverlays,
}: {
  mediaItem: MediaItem;
  mediaUrls?: Record<string, string>;
  className?: string;
  /** Render overlays immediately (e.g. in a hover peek) instead of waiting
   * for MediaPlayer's staggered in-viewport lazy load. */
  eagerOverlays?: boolean;
}) {
  const [measuredAspect, setMeasuredAspect] = useState<number | null>(null);

  // When a URL map is provided, wait for it to contain this slide's entries
  // before mounting MediaPlayer — otherwise MediaPlayer issues its own
  // signed-URL fetch per thumbnail, duplicating the caller's batch load.
  const urlsPending =
    !!mediaUrls &&
    (!mediaUrls[mediaItem.path] ||
      (mediaItem.type === "video" &&
        !!mediaItem.metadata?.thumbnailPath &&
        !mediaUrls[mediaItem.metadata.thumbnailPath]));

  const measureUrl =
    mediaItem.type === "video"
      ? mediaUrls?.[mediaItem.metadata?.thumbnailPath ?? ""]
      : mediaUrls?.[mediaItem.path];

  useEffect(() => {
    if (!measureUrl) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled && img.naturalWidth && img.naturalHeight) {
        setMeasuredAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = measureUrl;
    return () => {
      cancelled = true;
    };
  }, [measureUrl]);

  const dimensions = mediaItem.metadata?.dimensions;
  const metadataAspect =
    dimensions?.width && dimensions?.height
      ? dimensions.width / dimensions.height
      : null;
  const aspect = measuredAspect ?? metadataAspect ?? DEFAULT_ASPECT;

  return (
    <div
      className={cn("relative pointer-events-none", className)}
      style={{ aspectRatio: `${aspect}` }}
    >
      {/* Absolute fill so MediaPlayer's own aspect-video root can't impose
          its 16:9 min-content width on the wrapper's aspect ratio. */}
      {!urlsPending && (
        <div className="absolute inset-0">
          <MediaPlayer
            mediaItem={mediaItem}
            isControlsVisible={false}
            autoPlay={false}
            loop={false}
            muted={true}
            mediaUrls={mediaUrls}
            lazyLoadOverlays={!eagerOverlays}
            borderless={true}
          />
        </div>
      )}
    </div>
  );
}

export default SlidePreview;
